import { DineInRound, DineInRoundItem } from "../../interfaces/DineInObject";
import { ProductDetailsModal } from "../../interfaces/ProductObject";

// ─── Dine In — shared summary math ────────────────────────────────────────
// Status vocabulary plus every count/total derived from a session's rounds and
// items. Lives here rather than beside the diner feed because BOTH sides of
// dine-in need it: the diner's running bill (components/pages/dineIn) and the
// business-side kitchen board (components/features/Business/.../dinein). A
// features/ component importing from pages/ would be the wrong dependency
// direction, and duplicating the arithmetic would guarantee the two views
// eventually disagree about what a table owes.
//
// Everything here is pure — no React, no store — so it stays trivially
// testable and callable from converters, selectors and components alike.

/**
 * A ROUND's lifecycle, which is separate from an item's and uses its own
 * words:
 *
 *   DRAFT      the table is still adding to it — theirs alone, invisible work
 *   REQUESTED  the table pressed Order; it's now the kitchen's problem
 *   APPROVED / REJECTED / SERVED   the kitchen's verdict on the whole round
 *
 * Note APPROVED here against ACCEPTED for an item, and SERVED here against
 * DELIVERED for an item. The two vocabularies genuinely differ, so never reuse
 * DINE_IN_ITEM_STATUS for a round call or vice versa.
 *
 * "CLOSED" used to live here as the placed state and is gone: it isn't part of
 * this vocabulary at all, and the diner's Order button was still sending it.
 */
export const DINE_IN_ROUND_STATUS = {
  DRAFT: "DRAFT",
  REQUESTED: "REQUESTED",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  SERVED: "SERVED",
} as const;

/** Kept as its own constant because "is this a draft" is the single question
 *  every round predicate below reduces to. */
export const DRAFT_ROUND_STATUS = DINE_IN_ROUND_STATUS.DRAFT;

/**
 * True when a round is placed, i.e. belongs on a kitchen ticket. Anything not
 * recognisably a draft counts as placed — see DRAFT_ROUND_STATUS.
 */
export function isPlacedRound(status: string): boolean {
  return (status ?? "").trim().toUpperCase() !== DRAFT_ROUND_STATUS;
}

/**
 * Narrows a session to what the table has actually ORDERED — placed rounds and
 * the items inside them.
 *
 * This is the single definition of "placed", and it exists because having two
 * was a real bug: the kitchen board computed a table's item count and running
 * total from every item in the response, while the ticket for that same table
 * filtered drafts out. One table, two numbers, depending which screen you were
 * on. The business-side sessions endpoint DOES return draft rounds (confirmed
 * against a live response, 2026-08-11) despite the expectation that it
 * wouldn't, so anything on the kitchen side that counts, totals or renders
 * must narrow through here first.
 *
 * Items are matched by roundId rather than filtered on their own status: an
 * item's status currently echoes its round's, so trusting it here would drop
 * committed food.
 */
export function selectPlacedRounds(
  rounds: DineInRound[],
  roundItems: DineInRoundItem[]
): { rounds: DineInRound[]; items: DineInRoundItem[] } {
  const placed = rounds.filter((round) => isPlacedRound(round.status));
  const placedIds = new Set(placed.map((round) => round.roundId));
  return {
    rounds: placed,
    items: roundItems.filter((item) => placedIds.has(item.roundId)),
  };
}

/**
 * Per-ITEM kitchen lifecycle: ORDERED → ACCEPTED / REJECTED → SERVED.
 *
 * Confirmed as a lifecycle by product direction; the exact casing has NOT been
 * seen in a live response yet, only in the OpenAPI sample (where every string
 * field is the placeholder "string"). Reads therefore go through
 * normalizeDineInItemStatus, which upper-cases, so "Ordered" and "ORDERED"
 * both land correctly. If the backend turns out to use different words
 * entirely, this object is the single place to change them.
 */
export const DINE_IN_ITEM_STATUS = {
  /** Placed by the table, kitchen hasn't acted on it — the "needs action" state. */
  ORDERED: "ORDERED",
  /** Kitchen took it on. */
  ACCEPTED: "ACCEPTED",
  /** Kitchen refused it. Deliberately excluded from every total below. */
  REJECTED: "REJECTED",
  /** Delivered to the table. */
  SERVED: "SERVED",
} as const;

export type DineInItemStatus =
  (typeof DINE_IN_ITEM_STATUS)[keyof typeof DINE_IN_ITEM_STATUS];

/**
 * Wire spellings that mean the same thing as one of the canonical values.
 *
 * REQUESTED is what the live API actually sends for a placed item, against the
 * ORDERED this app was built around. Accepting both is not indecision — it's
 * the only way a status rename can't silently break the kitchen. And it WOULD
 * be silent: an unrecognised status still renders, just greyed and
 * unactionable, so the failure looks exactly like "the kitchen has nothing to
 * do" rather than like a bug. That cost a debugging round already.
 *
 * DELIVERED is here for a less happy reason: an earlier build of this client
 * WROTE DELIVERED when marking items served, before the vocabulary was
 * confirmed as SERVED. Those rows exist in the database and would otherwise
 * render as an unknown status forever. This alias is what keeps them readable
 * — delete it only once you're sure none are left.
 *
 * ACCEPTED and REJECTED are spelled the same either way.
 */
const DINE_IN_ITEM_STATUS_ALIASES: Record<string, string> = {
  REQUESTED: "ORDERED",
  DELIVERED: "SERVED",
};

/**
 * Upper-cases, trims, and folds known aliases onto the canonical value, so
 * every comparison in the app can be against DINE_IN_ITEM_STATUS regardless of
 * which spelling the backend used.
 *
 * Anything still unrecognised is returned as-is — an unknown status must stay
 * visible and stay billable rather than silently vanishing from a customer's
 * bill.
 */
export function normalizeDineInItemStatus(status: string | undefined | null): string {
  const raw = (status ?? "").trim().toUpperCase();
  return DINE_IN_ITEM_STATUS_ALIASES[raw] ?? raw;
}

/**
 * Canonical -> wire. The ONLY thing that should ever be sent to the backend.
 *
 * Reads are forgiving (several spellings fold to canonical) but writes cannot
 * be: exactly one string is correct, and sending the wrong one either fails or
 * — worse — succeeds in setting a status the app then treats as unknown, which
 * renders as a greyed, unactionable row that looks like an idle kitchen.
 *
 * SERVED used to be mapped to DELIVERED here and that was WRONG — the backend
 * wants SERVED. It's now absent, so it passes through unchanged. ACCEPTED and
 * REJECTED were always absent for the same reason. In practice that leaves
 * this map identity for everything the client currently writes, and it is kept
 * anyway as the single seam: the day an item status and its wire spelling
 * diverge again, this is the one place to express it, instead of hunting down
 * literals at call sites.
 *
 * ORDERED stays listed because that divergence is real and proven on the read
 * side, even though nothing writes it today.
 */
const DINE_IN_ITEM_STATUS_WIRE: Record<string, string> = {
  [DINE_IN_ITEM_STATUS.ORDERED]: "REQUESTED",
};

/** Translates a canonical status into the string the API expects. */
export function toWireDineInItemStatus(canonicalStatus: string): string {
  return DINE_IN_ITEM_STATUS_WIRE[canonicalStatus] ?? canonicalStatus;
}

/** True only for an explicitly REJECTED item. Unknown statuses are NOT rejected. */
export function isRejectedDineInItem(item: DineInRoundItem): boolean {
  return normalizeDineInItemStatus(item.status) === DINE_IN_ITEM_STATUS.REJECTED;
}

/**
 * Items that count toward money and plate counts — everything the kitchen
 * hasn't refused. A rejected dish was never served and must never appear on
 * the bill, so this filter sits in front of every total in this file.
 */
export function billableDineInItems(roundItems: DineInRoundItem[]): DineInRoundItem[] {
  return roundItems.filter((item) => !isRejectedDineInItem(item));
}

/** Items still awaiting a kitchen decision — drives the board's "to accept" badge. */
export function orderedItemCount(roundItems: DineInRoundItem[]): number {
  return roundItems.filter(
    (item) => normalizeDineInItemStatus(item.status) === DINE_IN_ITEM_STATUS.ORDERED
  ).length;
}

/**
 * The one round (if any) the table is still building — i.e. still DRAFT.
 *
 * This used to be "any round that isn't CLOSED", which was correct only while
 * CLOSED existed. Under the real vocabulary a placed round is REQUESTED, and
 * that test would have called it open — letting the diner keep adding items
 * into a round the kitchen had already been handed, and hiding the "Round N
 * ordered" divider for it. Open now means exactly one thing: not yet placed.
 *
 * The backend implicitly starts a new round the moment an item lands after the
 * last one was placed, so there's never more than one draft at a time — but
 * this still picks the highest roundNumber defensively in case that changes.
 */
export function findOpenRound(rounds: DineInRound[]): DineInRound | null {
  const open = rounds.filter((r) => !isPlacedRound(r.status));
  if (open.length === 0) return null;
  return open.reduce((latest, r) => (r.roundNumber > latest.roundNumber ? r : latest));
}

/** Total plates (sum of quantities) across the session, rejected items excluded. */
export function totalPlateCount(roundItems: DineInRoundItem[]): number {
  return billableDineInItems(roundItems).reduce(
    (sum, item) => sum + (item.quantity && item.quantity > 0 ? item.quantity : 1),
    0
  );
}

/** What a dine-in line is called and costs once its variant is taken into account. */
export interface ResolvedDineInLine {
  /** Product title, or "" while the product is still resolving. */
  title: string;
  /** "Full", "Half"... — undefined for a product ordered without a variant. */
  variantLabel?: string;
  /** "Biryani · Full" — what every screen shows for this line. */
  displayTitle: string;
  /** Variant price when the variant matches product.variants, else
   *  sellingPrice; null while the product is still resolving. */
  unitPrice: number | null;
}

/** productId -> product, built once per render/derivation so per-line lookups
 *  stay O(1) instead of scanning the product list for every item. */
export type DineInProductIndex = Map<string, ProductDetailsModal>;

export function buildProductIndex(products: ProductDetailsModal[]): DineInProductIndex {
  return new Map(products.map((p) => [p.productId, p]));
}

/**
 * The one place a dine-in line's name and price are computed, for callers that
 * already hold the product. A variant's own price wins when its id matches; an
 * unmatched or absent variantId falls back to the product's sellingPrice, so
 * pre-variant rows price exactly as before. `product` undefined means it is
 * still resolving.
 */
export function resolveProductLine(
  product: ProductDetailsModal | undefined,
  variantId?: string
): ResolvedDineInLine {
  if (!product) return { title: "", displayTitle: "", unitPrice: null };
  const variant = variantId
    ? product.variants?.find((v) => v.variantId === variantId)
    : undefined;
  const title = product.title ?? "";
  return {
    title,
    variantLabel: variant?.label,
    displayTitle: variant?.label ? `${title} · ${variant.label}` : title,
    unitPrice: variant ? variant.price : product.sellingPrice ?? null,
  };
}

/** Index-backed variant of resolveProductLine, for loops over many items. */
export function resolveDineInLine(
  item: Pick<DineInRoundItem, "productId" | "variantId">,
  index: DineInProductIndex
): ResolvedDineInLine {
  return resolveProductLine(index.get(item.productId), item.variantId);
}

/**
 * Identity of a dine-in line. Every grouping (converter fallback key, diner
 * feed, kitchen ticket, bill) starts from product + variant and appends its
 * own dimensions, so the next dimension is a one-line edit here rather than
 * four hand-written template strings.
 */
export function lineIdentityKey(
  item: Pick<DineInRoundItem, "productId" | "variantId">,
  ...extra: Array<string | undefined>
): string {
  return [item.productId, item.variantId ?? "", ...extra.map((e) => e ?? "")].join("__");
}

/** Plates still awaiting a kitchen decision — the server's 10020 rule blocks billing on these. */
export function pendingDineInPlateCount(roundItems: DineInRoundItem[]): number {
  return roundItems
    .filter((item) => normalizeDineInItemStatus(item.status) === DINE_IN_ITEM_STATUS.ORDERED)
    .reduce((sum, item) => sum + (item.quantity && item.quantity > 0 ? item.quantity : 1), 0);
}

/**
 * Running bill total across the whole session. Items whose product hasn't
 * resolved yet (product fetch still in flight) are excluded rather than
 * counted as ₹0, so the total doesn't dip while it loads. Rejected items are
 * excluded outright — the kitchen refused them, nobody pays for them.
 */
export function totalBillAmount(
  roundItems: DineInRoundItem[],
  products: ProductDetailsModal[]
): number {
  const index = buildProductIndex(products);
  return billableDineInItems(roundItems).reduce((sum, item) => {
    const { unitPrice } = resolveDineInLine(item, index);
    const qty = item.quantity && item.quantity > 0 ? item.quantity : 1;
    return unitPrice != null ? sum + unitPrice * qty : sum;
  }, 0);
}

/**
 * Plate count + ₹ total scoped to a single round — same "skip unresolved
 * products rather than count them as ₹0" and "rejected items don't count"
 * conventions as totalBillAmount. Used by the "Round N ordered" divider.
 *
 * NOTE: itemCount is the sum of quantities, not the number of rows. It used to
 * be rows, back when the converter aggregated items — which meant three dosas
 * read as "1 item" next to a ₹ total that charged for three. Now that items
 * are stored one-per-raw-entry, rows are an even worse proxy, so this counts
 * plates and finally agrees with the money beside it.
 */
export function roundOrderSummary(
  roundId: string,
  roundItems: DineInRoundItem[],
  products: ProductDetailsModal[]
): { itemCount: number; total: number } {
  const items = billableDineInItems(roundItems).filter((item) => item.roundId === roundId);
  const index = buildProductIndex(products);
  let itemCount = 0;
  let total = 0;
  for (const item of items) {
    const qty = item.quantity && item.quantity > 0 ? item.quantity : 1;
    itemCount += qty;
    const { unitPrice } = resolveDineInLine(item, index);
    if (unitPrice != null) total += unitPrice * qty;
  }
  return { itemCount, total };
}
